import {
  FC,
  memo,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MatchStatus } from "service";
import { BallBase } from "components/common/BallBase";
import BasketballBall from "../assets/basketball.svg";
import { SIDE } from "_constants";
import { isThreePointCode, isTwoPointCode } from "../constants";

const PATH_STOPPER_EVENTS: string[] = [];
const VIEW_W = 400;
const VIEW_H = 160;
const SMOOTH_MS = 350;
const BOUNCE_AMPLITUDE = 8; // px
const BOUNCE_PERIOD = 900; // ms
const STABLE_MS = 250; // ms

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
const isNum = (v: any) => typeof v === "number" && Number.isFinite(v);

type BallProps = {
  status: MatchStatus;
  ballElementRef?: RefObject<SVGImageElement | null>;
};

export const Ball: FC<BallProps> = memo(({ status, ballElementRef }) => {
  const rawX = (status as any)?.x ?? (status as any)?.X;
  const rawY = (status as any)?.y ?? (status as any)?.Y;
  const nsc = String((status as any)?.nsc ?? "");
  const side = (status as any)?.side;

  const hasXY = isNum(rawX) && isNum(rawY);

  // last valid target (initial default: center)
  const lastValidTargetRef = useRef<{ x: number; y: number }>({
    x: 0.5,
    y: 0.5,
  });

  // save if xy is present
  if (hasXY) {
    lastValidTargetRef.current = {
      x: clamp01(Number(rawX)),
      y: clamp01(Number(rawY)),
    };
  }

  // --- target/drawing logic ---
  // targetForAnim: always defined target for animation and state calculations
  // shouldRender: determines whether to render BallBase
  let targetForAnim: { x: number; y: number } = lastValidTargetRef.current;
  let shouldRender = false;

  if (hasXY) {
    targetForAnim = { x: clamp01(Number(rawX)), y: clamp01(Number(rawY)) };
    shouldRender = true;
  } else if (nsc === "1078") {
    // FREE THROW: fixed fallback (to avoid breakage)
    const isHome = String(side) === String(SIDE.HOME);
    targetForAnim = { x: isHome ? 0.77 : 0.23, y: 0.5 };
    shouldRender = true;
  } else if (isTwoPointCode(nsc) || isThreePointCode(nsc)) {
    // 2P/3P: if no xy, use fixed fallback like Free Throw (not last position)
    const isHome = String(side) === String(SIDE.HOME);
    if (isTwoPointCode(nsc)) {
      // 2 Points: closer to the hoop / paint area
      targetForAnim = { x: isHome ? 0.7 : 0.3, y: 0.5 };
    } else {
      // 3 Points: beyond the arc
      targetForAnim = { x: isHome ? 0.65 : 0.35, y: 0.5 };
    }
    shouldRender = true;
  } else {
    // for other events, if no xy, ball should not be visible
    shouldRender = false;
    // still use a defined target for animation calculations (last target)
    targetForAnim = lastValidTargetRef.current;
  }

  // --- animation state ---
  const [pos, setPos] = useState(targetForAnim);
  const lastTargetRef = useRef(targetForAnim);
  const idleSinceRef = useRef<number>(Date.now());
  const rafRef = useRef<number | null>(null);
  const [tick, setTick] = useState(0);

  // smooth transition on target change
  const tx = targetForAnim.x;
  const ty = targetForAnim.y;

  useEffect(() => {
    const changed =
      Math.abs(tx - lastTargetRef.current.x) > 1e-6 ||
      Math.abs(ty - lastTargetRef.current.y) > 1e-6;
    if (!changed) return;

    lastTargetRef.current = { x: tx, y: ty };
    idleSinceRef.current = Date.now();

    const start = { ...pos };
    const startTs = performance.now();
    const dur = Math.max(0, SMOOTH_MS);

    const step = (now: number) => {
      const t = dur === 0 ? 1 : Math.min(1, (now - startTs) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setPos({
        x: start.x + (tx - start.x) * eased,
        y: start.y + (ty - start.y) * eased,
      });
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tx, ty]);

  // idle bounce (slight bounce when target is stable)
  useEffect(() => {
    const id = setInterval(() => {
      const stableFor = Date.now() - idleSinceRef.current;
      if (stableFor >= STABLE_MS) setTick((t) => (t + 1) % 1_000_000);
    }, 60);
    return () => clearInterval(id);
  }, []);

  const ballOffsetYPx = useMemo(() => {
    const stableFor = Date.now() - idleSinceRef.current;
    if (stableFor < STABLE_MS) return 0;
    const t = (Date.now() % BOUNCE_PERIOD) / BOUNCE_PERIOD;
    const sin = Math.sin(t * Math.PI * 2);
    return sin * BOUNCE_AMPLITUDE;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  const displayStatus = useMemo(
    () => ({ ...status, x: pos.x, y: pos.y }),
    [status, pos.x, pos.y]
  );

  // Drawing decision: hooks called unconditionally; render conditional
  return shouldRender ? (
    <BallBase
      status={displayStatus}
      ballImageHref={BasketballBall}
      viewW={VIEW_W}
      viewH={VIEW_H}
      ballSize={22}
      pathLineMax={5}
      pathStopperEvents={PATH_STOPPER_EVENTS}
      showShadowEllipse={true}
      extraPathDeps={[]}
      ballExtraOffsetYPx={ballOffsetYPx}
      shadowOffsetYPx={10}
      ballElementRef={ballElementRef}
    />
  ) : null;
});
